import React, { useState, useRef, useCallback, useEffect } from 'react';
import Webcam from 'react-webcam';
import { config } from '../config/config';
import { detectFaceOnImageElement, drawLandmarksOnCanvas, cleanup } from '../utils/faceLandmarks';
import { aiAnalysisService } from '../utils/apiClient';

const PhotoCapture = ({ nextStep, updateCapturedImage, appState, setError }) => {
  const [captureState, setCaptureState] = useState({
    mode: 'choose', // 'choose', 'instructions', 'camera', 'upload'
    isCapturing: false,
    capturedPhoto: null,
    showPreview: false,
    cameraError: null,
    countdown: null
  });

  const [localError, setLocalError] = useState(null);

  // Helper function to set both local and parent errors
  const showError = (message) => {
    setLocalError(message);
    setError(message);
    // Clear error after 10 seconds (longer duration)
    setTimeout(() => setLocalError(null), 10000);
  };

  const webcamRef = useRef(null);
  const fileInputRef = useRef(null);
  const previewImgRef = useRef(null);
  const overlayCanvasRef = useRef(null);
  const [analysisState, setAnalysisState] = useState({
    analyzing: false,
    analyzed: false,
    error: null,
  });

  // Camera constraints for mobile optimization
  const videoConstraints = {
    width: { ideal: 1280 },
    height: { ideal: 720 },
    facingMode: "user", // Front camera for selfies
    aspectRatio: 16/9
  };

  const capture = useCallback(() => {
    if (!webcamRef.current) {
      setError('Camera not available. Please try uploading a photo instead.');
      return;
    }

    setCaptureState(prev => ({ ...prev, isCapturing: true }));
    
    try {
      // Capture with dynamic sizing for better mobile support
      const video = webcamRef.current.video;
      const aspectRatio = video.videoWidth / video.videoHeight;
      const maxWidth = Math.min(1280, window.innerWidth * 2);
      const width = maxWidth;
      const height = Math.round(maxWidth / aspectRatio);
      
      const imageSrc = webcamRef.current.getScreenshot({
        width: width,
        height: height,
        screenshotFormat: 'image/jpeg',
        screenshotQuality: 0.92
      });

      // Un-mirror the captured image since we mirrored the preview
      let finalImageSrc = imageSrc;
      if (imageSrc) {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const img = new Image();

        img.onload = () => {
          canvas.width = img.width;
          canvas.height = img.height;

          // Flip horizontally to correct the mirrored preview
          ctx.scale(-1, 1);
          ctx.drawImage(img, -img.width, 0);

          finalImageSrc = canvas.toDataURL('image/jpeg', 0.92);

          if (finalImageSrc && finalImageSrc.length > 1000) {
            setCaptureState(prev => ({
              ...prev,
              capturedPhoto: finalImageSrc,
              showPreview: true,
              isCapturing: false
            }));
          } else {
            throw new Error('Failed to capture image or image too small');
          }
        };

        img.src = imageSrc;
        return; // Exit early since we're handling async image processing
      }
    } catch (error) {
      // HIPAA Compliance: Removed console logging
setError('Failed to capture photo. Please try again or upload a file.');
      setCaptureState(prev => ({ ...prev, isCapturing: false }));
    }
  }, [setError]);

  const startCaptureCountdown = () => {
    if (captureState.isCapturing || analysisState.analyzing || captureState.countdown !== null) return;
    setCaptureState(prev => ({ ...prev, countdown: 3 }));
  };

  useEffect(() => {
    if (captureState.countdown === null) return;

    if (captureState.countdown === 0) {
      setCaptureState(prev => ({ ...prev, countdown: null }));
      capture();
      return;
    }

    const timer = setTimeout(() => {
      setCaptureState(prev => ({ ...prev, countdown: prev.countdown - 1 }));
    }, 1000);

    return () => clearTimeout(timer);
  }, [captureState.countdown, capture]);

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    
    // If no file selected (user cancelled), go back to choose screen
    if (!file) {
      setCaptureState(prev => ({ ...prev, mode: 'choose' }));
      return;
    }

    // Show loading state
    setCaptureState(prev => ({ ...prev, isCapturing: true }));

    // Validate file type
    if (!file.type.startsWith('image/')) {
      showError('Please select a valid image file (JPG, PNG, etc.)');
      setCaptureState(prev => ({ ...prev, mode: 'choose', isCapturing: false }));
      return;
    }

    // Validate file size (max 6MB for optimal processing)
    const fileSizeMB = (file.size / (1024 * 1024)).toFixed(1);
    if (file.size > 6 * 1024 * 1024) {
      showError(`This image is too large (${fileSizeMB}MB). Please use a smaller image under 6MB or take a photo with your camera for best results.`);
      setCaptureState(prev => ({ ...prev, mode: 'choose', isCapturing: false }));
      return;
    }

    const reader = new FileReader();
    reader.onload = async (e) => {
      const result = e.target.result;

      if (result && result.length > 10000) {
        // No compression needed - file size already validated at 6MB limit
        setCaptureState(prev => ({
          ...prev,
          capturedPhoto: result,
          showPreview: true,
          isCapturing: false,
          mode: 'upload'
        }));
      } else {
        setError('Failed to process the uploaded image. Please try a different file.');
        setCaptureState(prev => ({ ...prev, mode: 'choose', isCapturing: false }));
      }
    };
    reader.onerror = () => {
      setError('Failed to read the image file. Please try again.');
      setCaptureState(prev => ({ ...prev, mode: 'choose', isCapturing: false }));
    };
    reader.readAsDataURL(file);
  };

  const confirmPhoto = () => {
    if (!captureState.capturedPhoto) {
      setError('No photo to confirm. Please capture or upload a photo first.');
      return;
    }

    // Extract base64 from data URL for API compatibility
    let base64Data = captureState.capturedPhoto;
    if (base64Data.startsWith('data:')) {
      // Remove the data URL prefix (e.g., "data:image/jpeg;base64,")
      base64Data = base64Data.split(',')[1];
    }

    // Validate base64 length
    if (base64Data.length < 10000) {
      setError('Image data is corrupted or too small. Please try capturing again.');
      return;
    }

    const imageData = {
      dataUrl: captureState.capturedPhoto, // For UI display
      base64: base64Data, // For API calls
      source: captureState.mode,
      timestamp: new Date().toISOString(),
      file: fileInputRef.current?.files?.[0] || null
    };

    updateCapturedImage(imageData);
    nextStep();
  };

  const retakePhoto = () => {
    // Clear canvas overlay
    if (overlayCanvasRef.current) {
      const ctx = overlayCanvasRef.current.getContext('2d');
      ctx.clearRect(0, 0, overlayCanvasRef.current.width, overlayCanvasRef.current.height);
    }

    setCaptureState(prev => ({
      ...prev,
      capturedPhoto: null,
      showPreview: false,
      mode: prev.mode === 'upload' ? 'choose' : prev.mode
    }));

    setAnalysisState({ analyzing: false, analyzed: false, error: null });

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const switchToCamera = () => {
    setLocalError(null); // Clear any errors when switching modes
    setCaptureState(prev => ({ ...prev, mode: 'camera', cameraError: null, countdown: null }));
  };

  const switchToInstructions = () => {
    setLocalError(null);
    setCaptureState(prev => ({ ...prev, mode: 'instructions', cameraError: null, countdown: null }));
  };

  const switchToUpload = () => {
    setLocalError(null); // Clear any errors when switching modes
    setCaptureState(prev => ({ ...prev, mode: 'upload' }));
    // Small delay to ensure state update, then trigger file dialog
    setTimeout(() => {
      fileInputRef.current?.click();
    }, 100);
  };

  const backToChoose = () => {
    setLocalError(null); // Clear any errors when going back
    setCaptureState(prev => ({ ...prev, mode: 'choose', cameraError: null, countdown: null }));
  };

  // Handle camera errors
  const handleCameraError = (error) => {
    // HIPAA Compliance: Removed console logging
setCaptureState(prev => ({
      ...prev,
      cameraError: 'Camera access denied or not available. Please use file upload instead.'
    }));
  };

  // Cleanup on component unmount
  useEffect(() => {
    return () => {
      cleanup();
    };
  }, []);

  // Choice Screen - Modern Consumer Design
  if (captureState.mode === 'choose') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-warm-50 via-skin-50 to-accent-pink/5 relative overflow-hidden">
        {/* Floating Background Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-20 -right-20 w-40 h-40 bg-gradient-to-br from-accent-pink/20 to-accent-purple/20 rounded-full blur-3xl animate-float"></div>
          <div className="absolute -bottom-20 -left-20 w-40 h-40 bg-gradient-to-tr from-accent-blue/20 to-skin-200/30 rounded-full blur-3xl animate-float" style={{animationDelay: '2s'}}></div>
        </div>

        {/* Modern Header */}
        <div className="relative z-10 bg-white/60 backdrop-blur-lg border-b border-white/20">
          <div className="max-w-lg mx-auto px-6 py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="step-indicator current">1</div>
                <div>
                  <div className="text-gray-900 font-semibold">Take a Photo</div>
                  <div className="text-xs text-gray-500">Step 1 of 4</div>
                </div>
              </div>
            </div>
            <div className="mt-4 w-full bg-gray-200 rounded-full h-1.5">
              <div className="bg-gradient-to-r from-accent-pink to-accent-purple h-1.5 rounded-full w-1/4 transition-all duration-500"></div>
            </div>
          </div>
        </div>

        <div className="flex-1 flex items-center justify-center px-6 py-12">
          <div className="max-w-md w-full">
            {/* Hero Section */}
            <div className="text-center mb-10 animate-fade-in-up">
              <div className="w-24 h-24 mx-auto bg-gradient-to-br from-accent-pink/20 to-accent-purple/20 rounded-3xl flex items-center justify-center mb-6">
                <svg className="w-12 h-12 text-accent-pink" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>

              <h1 className="heading-large !text-2xl md:!text-3xl mb-4">
                See What Your Skin Reveals
              </h1>
              <p className="text-warm text-base leading-relaxed mb-3">
                Get personalized insights in seconds - choose your preferred method
              </p>
              <p className="text-sm bg-cyan-50 text-cyan-700 px-4 py-2 rounded-full border border-cyan-200">
                Upload images smaller than 6MB or use the camera
              </p>
            </div>

            {/* Error Display */}
            {localError && (
              <div className="mb-6 animate-fade-in-up">
                <div className="bg-red-50 border border-red-200 rounded-2xl p-4 flex items-start space-x-3">
                  <div className="w-6 h-6 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <p className="text-red-800 text-sm font-medium">{localError}</p>
                  </div>
                  <button
                    onClick={() => setLocalError(null)}
                    className="text-red-400 hover:text-red-600 transition-colors"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
            )}

            {/* Modern Option Cards */}
            <div className="space-y-4 mb-10 animate-fade-in-up" style={{animationDelay: '0.2s'}}>
              <button
                onClick={switchToInstructions}
                className="w-full card-gradient p-6 text-left hover:shadow-soft-lg transition-all duration-300 transform hover:scale-[1.02] group border-0"
              >
                <div className="flex items-center">
                  <div className="w-14 h-14 bg-gradient-to-br from-accent-blue/20 to-accent-teal/20 rounded-2xl flex items-center justify-center mr-4 group-hover:from-accent-blue/30 group-hover:to-accent-teal/30 transition-all duration-300">
                    <svg className="w-7 h-7 text-accent-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-gray-900 text-lg mb-1">Take Photo</div>
                    <div className="text-gray-600 text-sm">Use your camera for instant capture</div>
                  </div>
                  <svg className="w-5 h-5 text-gray-400 group-hover:text-accent-pink group-hover:translate-x-1 transition-all duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </button>

              <button
                onClick={() => {
                  fileInputRef.current?.click();
                }}
                className="w-full card-gradient p-6 text-left hover:shadow-soft-lg transition-all duration-300 transform hover:scale-[1.02] group border-0"
              >
                <div className="flex items-center">
                  <div className="w-14 h-14 bg-gradient-to-br from-accent-pink/20 to-accent-purple/20 rounded-2xl flex items-center justify-center mr-4 group-hover:from-accent-pink/30 group-hover:to-accent-purple/30 transition-all duration-300">
                    <svg className="w-7 h-7 text-accent-pink" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-gray-900 text-lg mb-1">Upload Photo</div>
                    <div className="text-gray-600 text-sm">Choose from your gallery</div>
                  </div>
                  <svg className="w-5 h-5 text-gray-400 group-hover:text-accent-pink group-hover:translate-x-1 transition-all duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </button>
            </div>

            {/* Privacy Summary */}
            <div className="bg-white/75 backdrop-blur-sm rounded-2xl p-5 border border-gray-200/80 shadow-soft animate-fade-in-up" style={{animationDelay: '0.3s'}}>
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 bg-gray-900 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M12 3l7 4v5c0 4.5-2.9 7.8-7 9-4.1-1.2-7-4.5-7-9V7l7-4z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M9.5 12.5l1.8 1.8 3.2-3.8" />
                  </svg>
                </div>
                <div className="flex-1">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-gray-500 mb-2">Private Photo Handling</p>
                  <h3 className="font-semibold text-gray-900 text-base mb-3">Your photo is used once for analysis and then removed.</h3>
                  <div className="space-y-2">
                    <div className="flex items-start gap-2 text-sm text-gray-600 leading-relaxed">
                      <span className="mt-1 h-1.5 w-1.5 rounded-full bg-emerald-500 flex-shrink-0"></span>
                      <span>We do not keep your image after the analysis is completed.</span>
                    </div>
                    <div className="flex items-start gap-2 text-sm text-gray-600 leading-relaxed">
                      <span className="mt-1 h-1.5 w-1.5 rounded-full bg-accent-blue flex-shrink-0"></span>
                      <span>Only the report you choose to save stays on your device in Downloads.</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileUpload}
              accept="image/*"
              className="hidden"
            />
          </div>
        </div>
      </div>
    );
  }

  if (captureState.mode === 'instructions') {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Before We Begin</h2>
        <p className="text-gray-500 mb-8">For the best analysis results:</p>

        <div className="space-y-4 w-full max-w-sm mb-10">
          {[
            { icon: '👓', text: 'Remove glasses & spectacles' },
            { icon: '💡', text: 'Face a window or good light source' },
            { icon: '💄', text: 'Remove makeup for accurate results' },
            { icon: '💇', text: 'Pull hair away from your face' },
            { icon: '😐', text: 'Keep a neutral, relaxed expression' }
          ].map((item, i) => (
            <div key={i} className="flex items-center gap-4 p-3 bg-gray-50 rounded-xl">
              <span className="text-2xl">{item.icon}</span>
              <span className="text-gray-700 font-medium">{item.text}</span>
            </div>
          ))}
        </div>

        <button onClick={switchToCamera} className="w-full max-w-sm bg-gray-900 text-white py-4 rounded-2xl font-semibold text-lg">
          I'm Ready
        </button>

        <button onClick={backToChoose} className="mt-4 text-sm text-gray-500 underline underline-offset-2">
          Back
        </button>
      </div>
    );
  }

  // Upload Mode - Manual File Selection (fallback)
  if (captureState.mode === 'upload' && !captureState.isCapturing && !captureState.showPreview) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full">
          <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
            <div className="w-16 h-16 mx-auto bg-green-100 rounded-xl flex items-center justify-center mb-6">
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
            </div>
            
            <h2 className="text-2xl font-bold text-slate-900 mb-2">
              Select Your Photo
            </h2>
            <p className="text-slate-600 mb-8">
              Choose an image file from your device for skin analysis
            </p>

            <div className="space-y-4">
              <button
                onClick={() => fileInputRef.current?.click()}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-4 px-6 rounded-xl transition-colors"
              >
                Browse Files
              </button>
              
              <button
                onClick={backToChoose}
                className="w-full bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold py-4 px-6 rounded-xl transition-colors"
              >
                ← Back to Options
              </button>
            </div>

            <div className="mt-6 text-sm text-slate-500">
              <p>Supported: JPG, PNG, WebP • Max size: 6MB</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Upload Mode - Loading Screen
  if (captureState.mode === 'upload' && captureState.isCapturing && !captureState.showPreview) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center">
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <div className="w-16 h-16 mx-auto bg-blue-100 rounded-xl flex items-center justify-center mb-6">
              <svg className="w-8 h-8 text-blue-600 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </div>
            
            <h2 className="text-2xl font-bold text-slate-900 mb-2">Processing Image</h2>
            <p className="text-slate-600">
              Please wait while we prepare your photo for analysis...
            </p>
            
            <div className="mt-6 flex justify-center">
              <div className="flex space-x-1">
                <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce"></div>
                <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
                <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Camera Mode - Modern Full Screen Interface
  if (captureState.mode === 'camera' && !captureState.showPreview) {
    return (
      <div className="min-h-screen bg-black relative overflow-hidden">
        {/* Full Screen Camera */}
        <div className="absolute inset-0">
          {captureState.cameraError ? (
            <div className="flex items-center justify-center h-full text-white p-6">
              <div className="text-center max-w-md mx-auto">
                <div className="w-20 h-20 mx-auto mb-6 bg-red-500/20 rounded-full flex items-center justify-center">
                  <svg className="w-10 h-10 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold mb-3 text-white">Camera Access Required</h3>
                <p className="text-gray-300 text-base mb-6 leading-relaxed">
                  Please allow camera access to capture your photo for skin analysis
                </p>
                <button
                  onClick={backToChoose}
                  className="bg-white text-gray-900 px-8 py-3 rounded-2xl font-semibold hover:bg-gray-100 transition-all transform hover:scale-105"
                >
                  ← Choose Different Option
                </button>
              </div>
            </div>
          ) : (
            <>
              <Webcam
                ref={webcamRef}
                audio={false}
                screenshotFormat="image/jpeg"
                videoConstraints={videoConstraints}
                onUserMediaError={handleCameraError}
                className="w-full h-full object-cover"
                style={{ transform: 'scaleX(-1)' }} // Mirror the camera for natural selfie view
              />

              {/* Face Alignment Guide */}
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="relative">
                  <div className="w-72 h-96 sm:w-80 sm:h-[26rem] border-2 border-white/60 rounded-[50%] flex items-center justify-center relative bg-black/5">
                    <div className="w-64 h-80 sm:w-72 sm:h-96 border border-white/35 rounded-[50%]" />
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-3 h-3 bg-white/75 rounded-full animate-pulse" />
                  </div>

                  {captureState.countdown !== null ? (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-24 h-24 rounded-full bg-black/60 border border-white/40 backdrop-blur-md flex items-center justify-center">
                        <span className="text-4xl font-bold text-white">{captureState.countdown}</span>
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>

              {/* Modern Top Bar */}
              <div className="absolute top-0 left-0 right-0 z-10 p-6">
                <div className="flex items-center justify-between">
                  {/* Back Button */}
                  <button
                    onClick={backToChoose}
                    className="w-12 h-12 bg-black/50 backdrop-blur-md rounded-full flex items-center justify-center hover:bg-black/70 transition-all transform hover:scale-105"
                  >
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>

                  {/* Step Indicator */}
                  <div className="bg-black/60 backdrop-blur-md rounded-2xl px-4 py-2 border border-white/20">
                    <p className="text-white text-sm font-medium">Step 1 of 4</p>
                  </div>
                </div>

                <div className="mt-4 flex justify-center">
                  <div className="bg-black/60 backdrop-blur-md rounded-xl px-4 py-2 border border-white/20 shadow-lg text-center">
                    <p className="text-white text-sm font-semibold">Align your full face in the oval frame</p>
                    <p className="text-gray-300 text-xs">Hold still • Neutral expression • Good light</p>
                  </div>
                </div>
              </div>

              {/* Enhanced Bottom Controls */}
              <div className="absolute bottom-0 left-0 right-0 p-8">
                <div className="flex items-center justify-center space-x-12">
                  {/* Upload Alternative */}
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="w-14 h-14 bg-white/70 backdrop-blur-md rounded-full flex items-center justify-center hover:bg-white/90 transition-all transform hover:scale-105 border-2 border-white/60 shadow-lg"
                  >
                    <svg className="w-7 h-7 text-gray-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </button>

                  {/* Main Capture Button */}
                  <button
                    onClick={startCaptureCountdown}
                    disabled={captureState.isCapturing || analysisState.analyzing || captureState.countdown !== null}
                    className="w-28 h-28 bg-white rounded-full flex items-center justify-center shadow-2xl hover:scale-105 active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed relative animate-pulse"
                  >
                    {captureState.isCapturing || analysisState.analyzing ? (
                      <div className="w-8 h-8 border-3 border-gray-300 border-t-gray-700 rounded-full animate-spin"></div>
                    ) : (
                      <>
                        <div className="w-24 h-24 bg-gray-900 rounded-full flex items-center justify-center">
                          <div className="w-20 h-20 bg-white rounded-full"></div>
                        </div>
                        {/* Pulse effect */}
                        <div className="absolute inset-0 w-28 h-28 bg-white/30 rounded-full animate-ping"></div>
                      </>
                    )}
                  </button>

                  {/* Flash/Settings placeholder */}
                  <div className="w-14 h-14 opacity-50">
                    {/* Spacer for symmetry */}
                  </div>
                </div>

                {/* Bottom instruction */}
                <div className="text-center mt-6">
                  <p className="text-white text-sm font-medium drop-shadow-lg">
                    {captureState.countdown !== null ? 'Capturing...' : 'Tap to start 3-second countdown'}
                  </p>
                </div>
              </div>

              {/* Enhanced Processing Overlay */}
              {(analysisState.analyzing || captureState.isCapturing) && (
                <div className="absolute inset-0 bg-black/90 backdrop-blur-sm flex items-center justify-center z-20">
                  <div className="text-center text-white max-w-sm mx-auto p-8">
                    <div className="relative mb-8">
                      <div className="w-32 h-32 border-4 border-blue-500/30 border-t-blue-500 rounded-full animate-spin mx-auto"></div>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-20 h-20 bg-blue-500/20 rounded-full flex items-center justify-center">
                          <svg className="w-10 h-10 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </div>
                      </div>
                    </div>
                    <h3 className="text-2xl font-semibold mb-3">Processing Image</h3>
                    <p className="text-white/80 mb-6 text-base leading-relaxed">
                      Preparing your photo for AI analysis...
                    </p>
                    <div className="bg-white/10 rounded-full h-2 w-full overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-blue-500 to-purple-500 rounded-full animate-pulse" style={{ width: '75%' }}></div>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Hidden file input for upload fallback */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleFileUpload}
        />
      </div>
    );
  }

  // Preview Mode - Professional Design
  if (captureState.showPreview) {
    return (
      <div className="min-h-screen bg-slate-50">
        {/* Progress Header */}
        <div className="bg-white border-b border-slate-200">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-green-600 rounded-full flex items-center justify-center text-white text-sm">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
                <span className="text-slate-900 font-medium">Photo Captured</span>
              </div>
              <div className="text-sm text-slate-500">Step 1 of 4</div>
            </div>
            <div className="mt-3 w-full bg-slate-200 rounded-full h-2">
              <div className="bg-green-600 h-2 rounded-full w-1/4"></div>
            </div>
          </div>
        </div>

        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-12">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-slate-900 mb-2">
              Photo Preview
            </h1>
            <p className="text-lg text-slate-600">
              Review your photo before proceeding to analysis
            </p>
          </div>

          {/* Photo Preview with overlay */}
          <div className="bg-white rounded-3xl shadow-lg overflow-hidden mb-8">
            <div className="relative aspect-[4/3] bg-slate-100">
              <img
                ref={previewImgRef}
                src={captureState.capturedPhoto}
                alt="Captured"
                className="w-full h-full object-contain"
                style={{ maxHeight: '100%', objectFit: 'contain' }}
                onLoad={async () => {
                  // Kick off background analysis when the image is ready
                  const img = previewImgRef.current;
                  const canvas = overlayCanvasRef.current;
                  if (!img || !canvas) return;

                  setAnalysisState({ analyzing: true, analyzed: false, error: null });

                  try {
                    // Wait for image to be fully loaded
                    await new Promise(resolve => {
                      if (img.complete && img.naturalWidth > 0) {
                        resolve();
                      } else {
                        img.addEventListener('load', resolve, { once: true });
                      }
                    });

                    // Small delay to ensure image is fully rendered
                    await new Promise(resolve => setTimeout(resolve, 100));

                    // Get image container and actual image dimensions
                    const imgContainer = img.parentElement;
                    const containerRect = imgContainer.getBoundingClientRect();

                    // Calculate actual displayed image size within container
                    const imgAspectRatio = img.naturalWidth / img.naturalHeight;
                    const containerAspectRatio = containerRect.width / containerRect.height;

                    let displayWidth, displayHeight, offsetX = 0, offsetY = 0;

                    if (imgAspectRatio > containerAspectRatio) {
                      // Image is wider - fits container width
                      displayWidth = containerRect.width;
                      displayHeight = containerRect.width / imgAspectRatio;
                      offsetY = (containerRect.height - displayHeight) / 2;
                    } else {
                      // Image is taller - fits container height
                      displayHeight = containerRect.height;
                      displayWidth = containerRect.height * imgAspectRatio;
                      offsetX = (containerRect.width - displayWidth) / 2;
                    }

                    // Set canvas dimensions to match the actual displayed image
                    canvas.width = displayWidth;
                    canvas.height = displayHeight;

                    // Position canvas to perfectly overlay the displayed image
                    canvas.style.position = 'absolute';
                    canvas.style.top = offsetY + 'px';
                    canvas.style.left = offsetX + 'px';
                    canvas.style.width = displayWidth + 'px';
                    canvas.style.height = displayHeight + 'px';

                    // Detect face landmarks
                    const lms = await detectFaceOnImageElement(img);

                    if (lms && lms.length > 0) {
                      // Draw white face mesh with proper scaling
                      drawLandmarksOnCanvas(lms, canvas, { color: '#FFFFFF', radius: 1.0 });
                      setAnalysisState({ analyzing: false, analyzed: true, error: null });
                    } else {
                      setAnalysisState({ analyzing: false, analyzed: false, error: 'No face detected' });
                    }
                  } catch (e) {
                    setAnalysisState({ analyzing: false, analyzed: false, error: 'Face analysis failed' });
                  }
                }}
              />
              <canvas
                ref={overlayCanvasRef}
                className="absolute inset-0 w-full h-full pointer-events-none"
              />
              {/* Analyzing overlay */}
              {analysisState.analyzing && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                  <div className="flex items-center space-x-3 text-white">
                    <div className="w-6 h-6 border-4 border-white border-t-transparent rounded-full animate-spin" />
                    <span className="font-medium">Analyzing face…</span>
                  </div>
                </div>
              )}
            </div>
            
            <div className="p-6">
              {analysisState.analyzed ? (
                <div className="flex items-center justify-center space-x-2 text-green-600 mb-4">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span className="font-medium">Face detected - ready for analysis</span>
                </div>
              ) : analysisState.analyzing ? (
                <div className="flex items-center justify-center space-x-2 text-slate-500 mb-4">
                  <div className="w-4 h-4 border-2 border-slate-500 border-t-transparent rounded-full animate-spin" />
                  <span className="font-medium">Preparing analysis…</span>
                </div>
              ) : (
                <div className="flex items-center justify-center space-x-2 text-blue-600 mb-4">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span className="font-medium">Photo ready for analysis</span>
                </div>
              )}

              <p className="text-slate-600 text-center">
                This photo will be analyzed by our AI system to provide detailed skin insights
              </p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="space-y-4">
            <button
              onClick={confirmPhoto}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-4 px-8 rounded-xl text-lg transition-colors"
            >
              Continue to Analysis →
            </button>
            
            <button
              onClick={retakePhoto}
              className="w-full bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold py-4 px-8 rounded-xl text-lg transition-colors"
            >
              Retake Photo
            </button>
          </div>

          <div className="mt-8 text-center">
            <div className="inline-flex items-center px-4 py-2 bg-blue-50 rounded-lg text-sm text-blue-700">
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              Your photos and analysis results are NOT stored. Only contact information is encrypted and stored securely for appointment coordination (with your consent)
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default PhotoCapture;
